/**
 * Realistic Indian-context mock data for every lifestyle connector.
 *
 * Goal: every connector demos full-fidelity UX in DEV/mock mode without
 * a single network call. India-localized brands, prices in paise, INR.
 * No PII; all names are fictional Indian-name pairings.
 */
import type {
  WardrobeOrderItem,
  ContactSeed,
  CalendarEvent,
  InteractionStat,
  Subscription,
  TripRecord,
  TravelBooking,
  FoodOrder,
  WorkProfile,
  PhotoSlim,
  Provenance,
} from './types.ts';
import { paise } from './types.ts';

const now = Date.now();
const day = 86_400_000;
const iso = (offset_days: number): string => new Date(now - offset_days * day).toISOString().slice(0, 10);
const isoFull = (offset_days: number): string => new Date(now - offset_days * day).toISOString();

const prov = (connector: Provenance['connector'], idx: number): Provenance => ({
  connector,
  source_id: `mock-${connector}-${idx}`,
  fetched_at: now,
  raw_hash: 'mock_no_hash',
});

export const MOCK_WARDROBE_ORDERS: readonly WardrobeOrderItem[] = [
  {
    merchant: 'myntra',
    order_id: 'MYN-118832441',
    order_date: iso(187),
    total: paise(184900),
    items: [
      { title: 'Levi’s 511 Slim Jeans — Indigo', brand: 'Levi’s', category: 'bottoms', qty: 1, unit_price: paise(184900), photo_url: 'https://assets.myntassets.com/v1/levis-511-indigo.jpg', size: '32', color: 'indigo' },
    ],
    provenance: prov('gmail-orders', 1),
  },
  {
    merchant: 'ajio',
    order_id: 'AJ-2031999102',
    order_date: iso(122),
    total: paise(269700),
    items: [
      { title: 'Tasva Bandhgala Kurta — Off-White', brand: 'Tasva', category: 'ethnic', qty: 1, unit_price: paise(269700), photo_url: 'https://assets.ajio.com/tasva-bandhgala-offwhite.jpg', size: 'M', color: 'off-white' },
    ],
    provenance: prov('gmail-orders', 2),
  },
  {
    merchant: 'amazon',
    order_id: '402-7491238-1129824',
    order_date: iso(98),
    total: paise(99800),
    items: [
      { title: 'Allen Solly Polo — Navy', brand: 'Allen Solly', category: 'tops', qty: 2, unit_price: paise(49900), photo_url: 'https://m.media-amazon.com/allen-solly-polo-navy.jpg', size: 'L', color: 'navy' },
    ],
    provenance: prov('gmail-orders', 3),
  },
  {
    merchant: 'myntra',
    order_id: 'MYN-119001442',
    order_date: iso(64),
    total: paise(329900),
    items: [
      { title: 'Adidas Ultraboost Light — Core Black', brand: 'Adidas', category: 'shoes', qty: 1, unit_price: paise(329900), photo_url: 'https://assets.myntassets.com/adidas-ub-light-black.jpg', size: 'UK 9', color: 'core black' },
    ],
    provenance: prov('gmail-orders', 4),
  },
  {
    merchant: 'nykaa',
    order_id: 'NYK-441923810',
    order_date: iso(41),
    total: paise(214900),
    items: [
      { title: 'Forest Essentials Silk Robe — Ivory', brand: 'Forest Essentials', category: 'loungewear', qty: 1, unit_price: paise(214900), photo_url: 'https://images.nykaa.com/fe-silk-robe-ivory.jpg', size: 'S', color: 'ivory' },
    ],
    provenance: prov('gmail-orders', 5),
  },
  {
    merchant: 'tata-cliq',
    order_id: 'TCQ-93021114',
    order_date: iso(28),
    total: paise(429900),
    items: [
      { title: 'Tommy Hilfiger Linen Shirt — Bone', brand: 'Tommy Hilfiger', category: 'tops', qty: 1, unit_price: paise(429900), photo_url: 'https://img.tatacliq.com/tommy-linen-bone.jpg', size: 'M', color: 'bone' },
    ],
    provenance: prov('gmail-orders', 6),
  },
  {
    merchant: 'ajio',
    order_id: 'AJ-2102991334',
    order_date: iso(12),
    total: paise(189900),
    items: [
      { title: 'H&M Wool-Blend Overcoat — Charcoal', brand: 'H&M', category: 'outerwear', qty: 1, unit_price: paise(189900), photo_url: 'https://assets.ajio.com/hm-overcoat-charcoal.jpg', size: 'L', color: 'charcoal' },
    ],
    provenance: prov('gmail-orders', 7),
  },
];

export const MOCK_CONTACTS: readonly ContactSeed[] = [
  { external_id: 'gc-001', name: 'Mausi Sushma', phones: ['+91 98101 41023'], emails: [], birthday: '1962-09-14', relation_hint: 'family', source: 'gcontacts', provenance: prov('gcontacts', 1) },
  { external_id: 'gc-002', name: 'Raunak Mehra', phones: ['+91 99102 33144'], emails: ['raunak.mehra@adaptivai.co'], relation_hint: 'work', source: 'gcontacts', provenance: prov('gcontacts', 2) },
  { external_id: 'gc-003', name: 'Gauri Sharma', phones: ['+91 99100 71190'], emails: [], birthday: '2024-01-22', relation_hint: 'family', source: 'gcontacts', provenance: prov('gcontacts', 3) },
  { external_id: 'gc-004', name: 'Aman Banerjee', phones: ['+91 80889 12005'], emails: ['aman.b@axa.com'], relation_hint: 'work', source: 'gcontacts', provenance: prov('gcontacts', 4) },
  { external_id: 'gc-005', name: 'Tushar Kapoor', phones: ['+91 98213 88010'], emails: ['tushar.kapoor@axa.com'], relation_hint: 'work', source: 'gcontacts', provenance: prov('gcontacts', 5) },
  { external_id: 'gc-006', name: 'Rohit Singh — College', phones: ['+91 88899 02201'], emails: [], relation_hint: 'friend', source: 'gcontacts', provenance: prov('gcontacts', 6) },
  { external_id: 'gc-007', name: 'Naina Kapur', phones: ['+91 99933 12101'], emails: ['naina.k@oai.com'], relation_hint: 'mentor', source: 'gcontacts', provenance: prov('gcontacts', 7) },
  { external_id: 'gc-008', name: 'Bhabhi Riya', phones: ['+91 99100 71101'], emails: [], anniversary: '2024-11-21', relation_hint: 'family', source: 'gcontacts', provenance: prov('gcontacts', 8) },
  { external_id: 'gc-009', name: 'Mom', phones: ['+91 98101 99921'], emails: ['mom.sharma@gmail.com'], relation_hint: 'family', source: 'gcontacts', provenance: prov('gcontacts', 9) },
  { external_id: 'gc-010', name: 'Reebjyoti Das', phones: ['+91 70022 11801'], emails: ['reeb.das@axa.com'], relation_hint: 'work', source: 'gcontacts', provenance: prov('gcontacts', 10) },
];

export const MOCK_CALENDAR_EVENTS: readonly CalendarEvent[] = [
  { external_id: 'cal-1', title: 'Mausi Sushma · Birthday', start: '2026-09-14', kind: 'birthday', recurrence: 'yearly', provenance: prov('gcalendar', 1) },
  { external_id: 'cal-2', title: 'Mom · Birthday', start: '2026-07-04', kind: 'birthday', recurrence: 'yearly', provenance: prov('gcalendar', 2) },
  { external_id: 'cal-3', title: 'Bhaiya + Riya · Anniversary', start: '2026-11-21', kind: 'anniversary', recurrence: 'yearly', provenance: prov('gcalendar', 3) },
  { external_id: 'cal-4', title: 'Gauri · Birthday', start: '2027-01-22', kind: 'birthday', recurrence: 'yearly', provenance: prov('gcalendar', 4) },
  { external_id: 'cal-5', title: 'AXA Bermuda QBR review', start: '2026-05-18T10:00:00+05:30', end: '2026-05-18T11:00:00+05:30', kind: 'event', attendees: ['raunak.mehra@adaptivai.co', 'reeb.das@axa.com'], provenance: prov('gcalendar', 5) },
  { external_id: 'cal-6', title: 'Diwali · 2026', start: '2026-11-08', kind: 'event', recurrence: 'yearly', provenance: prov('gcalendar', 6) },
  { external_id: 'cal-7', title: 'Naina catch-up · coffee', start: '2026-05-23T17:30:00+05:30', kind: 'event', attendees: ['naina.k@oai.com'], provenance: prov('gcalendar', 7) },
];

export const MOCK_INTERACTIONS: readonly InteractionStat[] = [
  { contact_key: '+91 98101 99921', month: '2026-04', messages_out: 32, messages_in: 38, calls_out: 6, calls_in: 11, gifts_mentioned: 0, longest_gap_days: 1, provenance: prov('wa-chat-export', 1) },
  { contact_key: '+91 98101 41023', month: '2026-04', messages_out: 4, messages_in: 2, calls_out: 0, calls_in: 1, gifts_mentioned: 0, longest_gap_days: 121, provenance: prov('wa-chat-export', 2) },
  { contact_key: '+91 99102 33144', month: '2026-04', messages_out: 88, messages_in: 94, calls_out: 12, calls_in: 9, gifts_mentioned: 1, longest_gap_days: 2, provenance: prov('wa-chat-export', 3) },
  { contact_key: '+91 99100 71101', month: '2026-04', messages_out: 18, messages_in: 22, calls_out: 0, calls_in: 1, gifts_mentioned: 2, longest_gap_days: 6, provenance: prov('wa-chat-export', 4) },
  { contact_key: '+91 88899 02201', month: '2026-04', messages_out: 3, messages_in: 5, calls_out: 0, calls_in: 0, gifts_mentioned: 0, longest_gap_days: 84, provenance: prov('wa-chat-export', 5) },
  { contact_key: '+91 99933 12101', month: '2026-04', messages_out: 14, messages_in: 11, calls_out: 2, calls_in: 1, gifts_mentioned: 1, longest_gap_days: 22, provenance: prov('wa-chat-export', 6) },
];

export const MOCK_SUBSCRIPTIONS: readonly Subscription[] = [
  { merchant: 'Netflix India', category: 'streaming', amount_monthly: paise(64900), billing_period: 'monthly', next_renewal: iso(-12), auto_renew: true, first_seen: iso(720), last_seen: iso(2), count: 24, provenance: prov('subscriptions', 1) },
  { merchant: 'Amazon Prime IN', category: 'streaming', amount_monthly: paise(124900 / 12), billing_period: 'yearly', next_renewal: iso(-189), auto_renew: true, first_seen: iso(1080), last_seen: iso(189), count: 4, provenance: prov('subscriptions', 2) },
  { merchant: 'YouTube Premium', category: 'streaming', amount_monthly: paise(12900), billing_period: 'monthly', next_renewal: iso(-7), auto_renew: true, first_seen: iso(540), last_seen: iso(1), count: 18, provenance: prov('subscriptions', 3) },
  { merchant: 'Cloudflare Pro', category: 'cloud', amount_monthly: paise(2000), billing_period: 'monthly', next_renewal: iso(-3), auto_renew: true, first_seen: iso(900), last_seen: iso(3), count: 30, provenance: prov('subscriptions', 4) },
  { merchant: 'Anthropic Max', category: 'saas', amount_monthly: paise(2079200), billing_period: 'monthly', next_renewal: iso(-19), auto_renew: true, first_seen: iso(120), last_seen: iso(19), count: 4, provenance: prov('subscriptions', 5) },
  { merchant: 'JioCinema Premium', category: 'streaming', amount_monthly: paise(9900), billing_period: 'monthly', next_renewal: iso(-26), auto_renew: true, first_seen: iso(180), last_seen: iso(26), count: 6, provenance: prov('subscriptions', 6) },
  { merchant: 'HDFC Smartbuy renew', category: 'utility', amount_monthly: paise(0), billing_period: 'yearly', next_renewal: iso(-211), auto_renew: false, first_seen: iso(900), last_seen: iso(211), count: 3, provenance: prov('subscriptions', 7) },
];

export const MOCK_TRIPS: readonly TripRecord[] = [
  { merchant: 'uber', external_id: 'UB-9911', start: isoFull(8), origin: 'Gurgaon Sector 54', destination: 'IGI T3', fare: paise(54600), distance_km: 26.4, tip: paise(2000), passengers: ['Raunak Mehra'], provenance: prov('uber-ola', 1) },
  { merchant: 'uber', external_id: 'UB-9920', start: isoFull(7), origin: 'IGI T3', destination: 'Gurgaon Sector 54', fare: paise(48200), distance_km: 25.1, provenance: prov('uber-ola', 2) },
  { merchant: 'ola', external_id: 'OL-77442', start: isoFull(21), origin: 'Greater Kailash', destination: 'CP', fare: paise(31000), distance_km: 12.7, tip: paise(1500), provenance: prov('uber-ola', 3) },
  { merchant: 'blusmart', external_id: 'BS-3091', start: isoFull(2), origin: 'Home', destination: 'IGI T3', fare: paise(58000), distance_km: 27.8, provenance: prov('uber-ola', 4) },
];

export const MOCK_BOOKINGS: readonly TravelBooking[] = [
  { merchant: 'mmt', pnr: 'MMT-EWG31', kind: 'flight', start: '2026-06-12T06:30:00+05:30', end: '2026-06-12T09:10:00+05:30', origin: 'DEL', destination: 'BOM', cost: paise(842000), travellers: ['Aditya Sharma', 'Mom'], provenance: prov('mmt-travel', 1) },
  { merchant: 'mmt', pnr: 'MMT-HTL992', kind: 'hotel', start: '2026-06-12', end: '2026-06-14', destination: 'Bombay · The Trident BKC', cost: paise(2940000), travellers: ['Aditya Sharma', 'Mom'], provenance: prov('mmt-travel', 2) },
  { merchant: 'irctc', pnr: '8224431902', kind: 'train', start: '2026-09-22T22:30:00+05:30', destination: 'Bandra Terminus', cost: paise(184000), travellers: ['Aditya Sharma'], provenance: prov('mmt-travel', 3) },
];

export const MOCK_FOOD: readonly FoodOrder[] = [
  { merchant: 'zomato', order_id: 'ZO-991023', placed_at: isoFull(1), restaurant: 'Burger Singh', total: paise(48000), items_count: 2, delivery_window: 'late_night', provenance: prov('zomato-swiggy', 1) },
  { merchant: 'swiggy', order_id: 'SW-411023', placed_at: isoFull(3), restaurant: 'Big Bowl', total: paise(38500), items_count: 1, delivery_window: 'dinner', provenance: prov('zomato-swiggy', 2) },
  { merchant: 'zomato', order_id: 'ZO-991311', placed_at: isoFull(7), restaurant: 'Theobroma', total: paise(112000), items_count: 4, delivery_window: 'evening', provenance: prov('zomato-swiggy', 3) },
];

export const MOCK_WORK: readonly WorkProfile[] = [
  { person_key: '+91 99102 33144', company: 'AdaptivAI', title: 'Engagement Lead — APAC', start: '2024-04-01', location: 'Gurgaon', seniority: 'lead', source: 'linkedin', provenance: prov('linkedin', 1) },
  { person_key: '+91 80889 12005', company: 'AXA XL', title: 'Senior Data Engineer', start: '2022-08-01', location: 'Bengaluru', seniority: 'senior', source: 'linkedin', provenance: prov('linkedin', 2) },
  { person_key: '+91 98213 88010', company: 'AXA XL', title: 'Pricing Analyst', start: '2023-02-01', location: 'Mumbai', seniority: 'mid', source: 'linkedin', provenance: prov('linkedin', 3) },
  { person_key: '+91 70022 11801', company: 'AXA XL', title: 'Director — Bermuda Reserving', start: '2018-04-01', location: 'Bermuda', seniority: 'director', source: 'linkedin', provenance: prov('linkedin', 4) },
  { person_key: '+91 99933 12101', company: 'OpenAI', title: 'Research Eng', start: '2025-03-01', location: 'San Francisco', seniority: 'senior', source: 'linkedin', provenance: prov('linkedin', 5) },
];

export const MOCK_PHOTOS: readonly PhotoSlim[] = [
  { external_id: 'gp-001', fetched_url: 'https://lh3.googleusercontent.com/closet/levis-indigo.jpg', captured_at: isoFull(187), mime: 'image/jpeg', bytes_estimate: 412_000, camera_model: 'iPhone 16 Pro', provenance: prov('gphotos', 1) },
  { external_id: 'gp-002', fetched_url: 'https://lh3.googleusercontent.com/closet/tasva-bandhgala.jpg', captured_at: isoFull(122), mime: 'image/jpeg', bytes_estimate: 488_000, camera_model: 'iPhone 16 Pro', provenance: prov('gphotos', 2) },
  { external_id: 'gp-003', fetched_url: 'https://lh3.googleusercontent.com/closet/adidas-ub.jpg', captured_at: isoFull(64), mime: 'image/jpeg', bytes_estimate: 502_000, camera_model: 'iPhone 16 Pro', provenance: prov('gphotos', 3) },
];
